/**
 * Runtime white-label theming, driven by the resolved agency's branding
 * (from the API), not mock data. Applies CSS variables consumed by app.css.
 */

export interface Branding {
  logo_url?: string;
  primary_color?: string;
  accent_color?: string;
  support_email?: string;
  support_phone?: string;
  footer_text?: string;
  privacy_url?: string;
}

const DEFAULTS = { primary: '#1b2a5b', accent: '#2e7d32' };

export function applyBranding(b: Branding | undefined | null): void {
  const primary = b?.primary_color || DEFAULTS.primary;
  const accent = b?.accent_color || DEFAULTS.accent;
  const r = document.documentElement.style;
  r.setProperty('--brand-primary', primary);
  r.setProperty('--brand-accent', accent);
  r.setProperty('--brand-primary-soft', `color-mix(in srgb, ${primary} 12%, white)`);
}

/** Initials for a name, max 2 letters (e.g. "Terah Mims Agency" -> "TM"). */
export function monogram(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * The full APEX PRO wordmark, traced from the brand-kit source of truth
 * (brand-kit/01-Full-Logo/SVG/apex-pro-black-transparent.svg) so the
 * letterforms are the real Kiona-set outlines rather than a webfont
 * substitute — the brand guide forbids recreating the logo in another
 * typeface, and brand-kit ships no font files.
 *
 * Paths inherit `currentColor`, so the same markup serves the black-on-light
 * and white-on-dark variants. The leading glyph IS the custom "A" mark, so
 * never pair this with a separate logomark.
 */
export const APEX_WORDMARK = `<svg class="apex-wordmark" viewBox="-40 -20 4970 718" role="img" aria-label="Apex Pro"><g transform="translate(0,658) scale(1,-1)" fill="currentColor"><path transform="translate(0,0)" d="M347.5 658.0 L34.6 0.0 L89.5 0.0 L370.1 609.6 L324.9 609.6 L605.5 0.0 L660.4 0.0 Z"/><path transform="translate(735,0)" d="M137 0V282H323Q380 282 428.5 307.0Q477 332 505.5 374.5Q534 417 534 466Q534 516 505.5 558.0Q477 600 428.5 625.0Q380 650 323 650H91L92 0ZM136 604H323Q368 604 406.0 585.5Q444 567 466.5 535.5Q489 504 489 466Q489 429 466.5 397.5Q444 366 406.0 347.5Q368 329 323 329H137Z"/><path transform="translate(1370,0)" d="M512 0V47H136V302H429V348H136V604H512V650H91V0Z"/><path transform="translate(2012,0)" d="M573 650 341 325 573 0H518L314 287L110 0H55L287 325L55 650H110L314 363L518 650Z"/><path transform="translate(2840,0)" d="M137 0V282H323Q380 282 428.5 307.0Q477 332 505.5 374.5Q534 417 534 466Q534 516 505.5 558.0Q477 600 428.5 625.0Q380 650 323 650H91L92 0ZM136 604H323Q368 604 406.0 585.5Q444 567 466.5 535.5Q489 504 489 466Q489 429 466.5 397.5Q444 366 406.0 347.5Q368 329 323 329H137Z"/><path transform="translate(3475,0)" d="M475 0 205 328H285Q359 328 405 347Q439 363 464.0 395.5Q489 428 489 467Q489 502 468.0 533.5Q447 565 408.0 585.0Q369 605 318 605H136V0H91V650H318Q364 650 404.0 635.5Q444 621 474 594Q503 568 518.5 535.0Q534 502 534 467Q534 412 501.0 368.5Q468 325 423 306Q392 292 362.5 288.5Q333 285 300 285L535 0Z"/><path transform="translate(4133,0)" d="M708 325Q708 415 664.0 491.5Q620 568 544.0 612.5Q468 657 379 657Q290 657 214.0 612.5Q138 568 94.0 491.5Q50 415 50 325Q50 235 94.0 158.5Q138 82 214.0 37.5Q290 -7 379 -7Q468 -7 544.0 37.5Q620 82 664.0 158.5Q708 235 708 325ZM662 325Q662 247 624.0 181.5Q586 116 521.0 77.5Q456 39 379 39Q302 39 237.0 77.5Q172 116 133.5 181.5Q95 247 95 325Q95 403 133.5 468.5Q172 534 237.0 572.5Q302 611 379 611Q456 611 521.0 572.5Q586 534 624.0 468.5Q662 403 662 325Z"/></g></svg>`;

export function logoHtml(
  name: string,
  opts: { sub?: string; accent?: boolean; logoUrl?: string; apex?: boolean } = {},
): string {
  // Apex-owned chrome: the wordmark already contains the custom "A" mark and
  // spells the name, so it replaces the mark+text lockup rather than sitting
  // beside it — pairing the two would double the mark and crowd its clear
  // space. `name` is unused here by design; `sub` carries the section label.
  if (opts.apex) {
    return `<span class="apex-lockup">${APEX_WORDMARK}${opts.sub ? `<small>${esc(opts.sub)}</small>` : ''}</span>`;
  }
  const mark = opts.logoUrl
    ? `<img class="logo-img" src="${esc(opts.logoUrl)}" alt="${esc(name)} logo" />`
    : `<span class="mark${opts.accent ? ' accent' : ''}">${monogram(name)}</span>`;
  return `<span class="logo">${mark}<span class="name">${esc(name)}${opts.sub ? `<small>${esc(opts.sub)}</small>` : ''}</span></span>`;
}

/** HTML-escape user-provided text before interpolating into innerHTML. */
export function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
